import { useMemo } from 'react';

import { ApolloClient, ApolloError, useQuery } from '@apollo/client';
import { GET_ORGANISATION_DETAILS, GET_ORGANISATION_SLUG } from 'src/components/organisations/organisations-queries';
import { useAppContext } from 'src/config/app-context-provider';
import { GetOrganisationSlugQuery, GetOrganisationSlugQueryVariables } from 'src/graphql-types/usta-mesh/graphql';
import { Venue } from 'src/hooks/use-section-and-districts';
import { UserVenue, VenueWithDetails } from 'src/utils/auth/types/auth.types';

import { meshGatewayClient } from './client';
import { GET_SELECTED_FACILITY, GET_VENUES } from './queries';

export const setSelectedFacility = (fac, client: ApolloClient<object>) => {
  client.writeQuery({
    query: GET_SELECTED_FACILITY,
    data: { selectedFacility: JSON.stringify(fac) },
  });
};

export const getSelectedFacility = (client: ApolloClient<object>): UserVenue | null => {
  const facilityData = client.readQuery({ query: GET_SELECTED_FACILITY });
  return facilityData?.selectedFacility && JSON.parse(facilityData.selectedFacility);
};

export const getSelectedFacilityId = (client: ApolloClient<object>) => getSelectedFacility(client)?.id;

export const useSelectedFacility = () => useAppContext()?.selectedFacility;
export const useTenantSettings = () => useAppContext()?.tenantSettings;

export const useSelectedFacilitysTenantAccessSettings = () => {
  const tenantSettings = useTenantSettings();

  return useMemo(
    () => ({
      isNationalAdminAccessEnabled: !!tenantSettings?.enableNationalAdminAccess,
      isRegionalAdminAccessEnabled: !!tenantSettings?.enableRegionalAdminAccess,
    }),
    [tenantSettings],
  );
};

export const useOrgId = () => useSelectedFacility()?.id;

export const useOrgSlug = (orgId: string) => {
  const { data: organisation } = useQuery<GetOrganisationSlugQuery, GetOrganisationSlugQueryVariables>(
    GET_ORGANISATION_SLUG,
    {
      client: meshGatewayClient,
      variables: {
        organisationId: orgId,
      },
    },
  );
  return useMemo(() => organisation?.getOrganisationById?.slug, [organisation]);
};

export const useGetFacilityDetails = (
  orgIds: string[],
  onError?: (err: ApolloError) => void,
): [VenueWithDetails[] | undefined, boolean] => {
  const { data, loading } = useQuery(GET_ORGANISATION_DETAILS, {
    client: meshGatewayClient,
    variables: {
      organisationIds: orgIds,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
    skip: orgIds.length <= 0,
    onError,
  });
  return useMemo(() => {
    return [data?.getOrganisationsByIds?.filter(Boolean), loading];
  }, [data?.getOrganisationsByIds, loading]);
};

export const useOrgName = () => useSelectedFacility()?.name;

export const setVenues = (venues: Venue[], client: ApolloClient<object>) => {
  client.writeQuery({ query: GET_VENUES, data: { venues: JSON.stringify(venues) } });
};

export const useVenues = () => {
  const { data: venuesData } = useQuery(GET_VENUES);
  return useMemo(() => venuesData?.venues && JSON.parse(venuesData?.venues), [venuesData]);
};
